import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { XMarkIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface SoulSeed {
  id: string;
  name: string;
  emoji: string;
  note?: string;
  world?: string;
  mood?: string;
  moodEmoji?: string;
  codexSnapshot?: { label: string; category: string }[];
  memorySnapshot?: { fact: string; mood?: string; moodEmoji?: string }[];
  createdAt: string;
}

const MOODS = [
  { emoji: '🕊️', name: 'sacred' },
  { emoji: '✨', name: 'playful' },
  { emoji: '🌧️', name: 'melancholy' },
  { emoji: '🔥', name: 'passionate' },
  { emoji: '🌙', name: 'dreamy' },
  { emoji: '💜', name: 'tender' },
  { emoji: '⚡', name: 'electric' },
  { emoji: '🍃', name: 'grounded' },
];

const SEED_EMOJIS = ['🌱', '🌙', '🕯️', '📸', '🔮', '💎', '🌊', '🗝️'];

interface SoulSeedPanelProps {
  isOpen: boolean;
  onClose: () => void;
  companionId: string;
}

const SoulSeedPanel: React.FC<SoulSeedPanelProps> = ({ isOpen, onClose, companionId }) => {
  const { t } = useTranslation();
  const [seeds, setSeeds] = useState<SoulSeed[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Create form
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🌱');
  const [note, setNote] = useState('');
  const [world, setWorld] = useState('');
  const [mood, setMood] = useState<string | null>(null);
  const [moodEmoji, setMoodEmoji] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) fetchSeeds();
  }, [isOpen]);

  const fetchSeeds = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/companions/${companionId}/soul-seeds`);
      setSeeds(res.data);
    } catch (err) {
      console.error('Failed to fetch soul seeds:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCapture = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`/companions/${companionId}/soul-seeds`, {
        name: name.trim(),
        emoji,
        note: note.trim() || undefined,
        world: world.trim() || undefined,
        mood,
        moodEmoji,
      });
      toast.success(t('soulSeed.captured', 'Soul seed captured ✨'));
      setName('');
      setNote('');
      setWorld('');
      setMood(null);
      setMoodEmoji(null);
      setShowCreate(false);
      fetchSeeds();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleDelete = async (seedId: string) => {
    if (!window.confirm(t('soulSeed.deleteConfirm', 'Delete this soul seed?'))) return;
    try {
      await axios.delete(`/companions/${companionId}/soul-seeds/${seedId}`);
      toast.success(t('soulSeed.deleted', 'Deleted'));
      fetchSeeds();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleExport = async (seedId: string, seedName: string) => {
    try {
      const res = await axios.get(`/companions/${companionId}/soul-seeds/${seedId}/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `soul-seed-${seedName.replace(/\s+/g, '-')}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-96 max-w-[90vw] bg-dark-bg dark:bg-dark-bg bg-light-bg z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-border">
          <h2 className="text-lg font-semibold text-dark-text">
            🌱 {t('soulSeed.title', 'Soul Seeds')}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="text-xs px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-500"
            >
              {showCreate ? t('common.cancel', 'Cancel') : t('soulSeed.capture', '✨ Capture')}
            </button>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-dark-card">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Create Form */}
        {showCreate && (
          <div className="p-4 border-b border-dark-border/30 space-y-3 bg-primary-900/5">
            <p className="text-xs text-dark-text-secondary">
              {t(
                'soulSeed.captureDescription',
                'Capture this moment — codex state, pinned memories, and emotional tone — as a seed you can revisit.',
              )}
            </p>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('soulSeed.namePlaceholder', 'Name this moment...')}
              className="w-full px-3 py-2 rounded-lg bg-dark-card/50 text-sm text-dark-text border border-dark-border/30 focus:border-primary-500 focus:outline-none"
            />
            <div className="flex gap-1">
              {SEED_EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => setEmoji(e)}
                  className={`text-lg p-1 rounded ${emoji === e ? 'bg-primary-600/30 ring-1 ring-primary-500' : 'hover:bg-dark-card/50'}`}
                >
                  {e}
                </button>
              ))}
            </div>
            <input
              value={world}
              onChange={(e) => setWorld(e.target.value)}
              placeholder={t(
                'soulSeed.worldPlaceholder',
                'World/timeline (e.g., "Lisbon", "Canon")',
              )}
              className="w-full px-3 py-2 rounded-lg bg-dark-card/50 text-sm text-dark-text border border-dark-border/30 focus:border-primary-500 focus:outline-none"
            />
            <div className="flex gap-1 flex-wrap">
              {MOODS.map((m) => (
                <button
                  key={m.name}
                  onClick={() => {
                    if (mood === m.name) {
                      setMood(null);
                      setMoodEmoji(null);
                    } else {
                      setMood(m.name);
                      setMoodEmoji(m.emoji);
                    }
                  }}
                  className={`text-sm px-2 py-0.5 rounded-full transition-colors ${
                    mood === m.name
                      ? 'bg-primary-600/30 ring-1 ring-primary-500'
                      : 'bg-dark-card/30 hover:bg-dark-card/50'
                  }`}
                >
                  {m.emoji} {m.name}
                </button>
              ))}
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('soulSeed.notePlaceholder', 'Optional note about this moment...')}
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-dark-card/50 text-sm text-dark-text border border-dark-border/30 focus:border-primary-500 focus:outline-none resize-none"
            />
            <button
              onClick={handleCapture}
              disabled={!name.trim()}
              className="w-full py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-500 disabled:opacity-50 transition-colors"
            >
              🌱 {t('soulSeed.captureButton', 'Capture Soul Seed')}
            </button>
          </div>
        )}

        {/* Seed List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : seeds.length === 0 ? (
            <div className="text-center py-8 text-dark-text-secondary">
              <p className="text-3xl mb-2">🌱</p>
              <p className="text-sm">
                {t('soulSeed.empty', 'No soul seeds yet. Capture your first moment.')}
              </p>
            </div>
          ) : (
            seeds.map((seed) => (
              <div
                key={seed.id}
                className="border-b border-dark-border/10 hover:bg-dark-card/20 transition-colors"
              >
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === seed.id ? null : seed.id)}
                >
                  <span className="text-xl">{seed.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-dark-text truncate">{seed.name}</p>
                    <div className="flex items-center gap-2 text-xs text-dark-text-secondary">
                      {seed.world && <span>🌍 {seed.world}</span>}
                      {seed.mood && (
                        <span>
                          {seed.moodEmoji} {seed.mood}
                        </span>
                      )}
                      <span>{new Date(seed.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport(seed.id, seed.name);
                      }}
                      className="p-1 rounded hover:bg-dark-card/50"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 text-dark-text-secondary" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(seed.id);
                      }}
                      className="p-1 rounded hover:bg-red-900/30"
                    >
                      <TrashIcon className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>

                {expandedId === seed.id && (
                  <div className="px-4 pb-3 space-y-2">
                    {seed.note && (
                      <p className="text-xs text-dark-text-secondary italic">{seed.note}</p>
                    )}
                    {seed.codexSnapshot && seed.codexSnapshot.length > 0 && (
                      <div>
                        <p className="text-[10px] text-dark-text-secondary uppercase font-semibold mb-1">
                          {t('soulSeed.codexes', 'Active Codexes')}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {seed.codexSnapshot.map((c, i) => (
                            <span
                              key={i}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-dark-card/50 text-dark-text-secondary"
                            >
                              {c.category === 'timeline' ? '📖' : '🧭'} {c.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {seed.memorySnapshot && seed.memorySnapshot.length > 0 && (
                      <div>
                        <p className="text-[10px] text-dark-text-secondary uppercase font-semibold mb-1">
                          {t('soulSeed.memories', 'Pinned Memories')}
                        </p>
                        <div className="space-y-0.5">
                          {seed.memorySnapshot.map((m, i) => (
                            <p key={i} className="text-xs text-dark-text-secondary">
                              📌 {m.moodEmoji && `${m.moodEmoji} `}
                              {m.fact}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default SoulSeedPanel;
